/**
 * Gear - UI for the Gear tab, the phase-1 goal tracker.
 *
 * Layout (per the approved mockup): goal strip (set progress, artifacts,
 * Command value), suggested-focus card with craft-vs-farm sourcing hints,
 * the 5-skill x 3-technique gear matrix (craftable/focus items render the
 * standard inventory card; owned and distant items collapse to strips),
 * and the faction's artifacts as cards. Logic lives in gear-core.js.
 */

import * as config from '../constants.js';
import { gameState } from './gameState.js';
import { findItem, findItemsById, getAllArtifacts } from './util.js';
import { createInventoryCard } from './inventory.js';
import { getGearMatrix, getGearStatus, getSuggestedFocus, getArtifactStatuses, describeMaterialSource } from './gear-core.js';
import { calculateCommandMarks, isCommandRevealed, isArtifactUnlocked } from './command-core.js';
import { registerSkillTabs, activeSkillTab, skillTabStrip } from './skillTabs.js';

// Field Kit and Technique Consumables each show one skill's three items rather
// than the full 5x3 matrix. Separate groups on purpose: the gear you are
// crafting is rarely the technique consumable you want next.
const KIT_TABS = 'gear-field-kit';
const CONSUMABLE_TABS = 'gear-consumables';
registerSkillTabs(KIT_TABS, () => renderGearTab());
registerSkillTabs(CONSUMABLE_TABS, () => renderGearTab());

// Three 13rem cards plus two 0.75rem gaps. Capping the grid's width rather than
// hard-coding `repeat(3, ...)` keeps it 3-up where there is room and lets it
// fall to 2 or 1 on a narrow pane instead of scrolling sideways.
const THREE_UP = 'grid gap-3 justify-start max-w-[40.5rem] mt-2';
const THREE_UP_TRACKS = 'grid-template-columns: repeat(auto-fit, 13rem);';

// Per-item expand/collapse overrides (persist across this page's re-renders;
// the card's own toggle is retargeted here so state survives crafting)
const gearCardOverrides = new Map(); // itemId -> expanded boolean
const renderedExpanded = new Map();  // itemId -> state actually rendered last
let focusCollapsed = false;          // Suggested-focus panel hidden by the player

function ownedGearCount(faction) {
    let owned = 0, total = 0;
    for (const column of getGearMatrix(faction)) {
        for (const { item } of column.techniques) {
            total++;
            if (getGearStatus(item.id).owned) owned++;
        }
    }
    return { owned, total };
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function goalBlock(label, valueHtml, note, extra = '') {
    return `
        <div class="flex-1 min-w-[210px] rounded-lg px-4 py-2 bg-game-accent/20 border border-blue-500">
            <div class="flex items-start">
                <div class="text-sm uppercase tracking-widest text-gray-100 flex-grow">${label}</div>
                <div class="text-base font-bold text-game-accent whitespace-nowrap tracking-tighter">${valueHtml}</div>
            </div>
            ${extra}
            <div class="text-xs text-gray-300 mt-3">${note}</div>
        </div>`;
}

function renderGoalStrip(container, faction) {
    const { owned, total } = ownedGearCount(faction);
    const pctGear = total > 0 ? Math.round((owned / total) * 100) : 0;
    
    const artifactsCrafted = gameState.character?.lifetime?.artifactsCrafted || 0;
    const pctArtifacts = total > 0 ? Math.round((artifactsCrafted / 17) * 100) : 0;

    const barTechGear = `
        <div class="h-2.5 rounded-full bg-gray-800 overflow-hidden mt-2">
            <div class="h-full bg-gradient-to-r from-blue-700 to-game-accent" style="width:${pctGear}%"></div>
        </div>`;

    const barArtifacts = `
        <div class="h-2.5 rounded-full bg-gray-800 overflow-hidden mt-2">
            <div class="h-full bg-gradient-to-r from-blue-700 to-game-accent" style="width:${pctArtifacts}%"></div>
        </div>`;

    let commandBlock = '';
    if (isCommandRevealed() && gameState.character) {
        const marks = calculateCommandMarks(gameState.character).total;
        commandBlock = `
            <div class="flex flex-col place-content-between flex-1 min-w-[210px] rounded-lg px-4 py-2" style="background:rgba(177,140,255,0.08); border:1px solid hsl(261, 35%, 58%);">
                <div class="flex items-start">
                    <div class="text-sm uppercase tracking-widest text-gray-300 flex-grow">Promotion Marks</div>
                    <div class="text-base font-bold whitespace-nowrap tracking-tighter" style="color:#b18cff">${marks}🎖️</div>
                </div>
                <div class="text-xs text-gray-300">Command awards marks based on crafted Skill Gear and Artifacts</div>
            </div>`;
    }

    container.innerHTML =
        goalBlock('Faction Tech Gear', `${owned} / ${total}`, `The core tools of the ${config.factionSkills[faction]?.name || faction} field kit`, barTechGear) +
        goalBlock('Artifacts Crafted', `${artifactsCrafted} / 17`, 'Artifact blueprints drop during T3 raids', barArtifacts) +
        commandBlock;
}

// Top-right ✕ that hides the focus panel; the matching re-open button lives
// next to the "Skill Gear increases…" text (#gear-focus-show).
const focusCloseBtn = `<button onclick="toggleGearFocus()" title="Hide suggested focus"
    class="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded bg-black/30 border border-gray-600 text-gray-400 hover:text-white hover:border-gray-400 text-sm leading-none">✕</button>`;

function renderFocusCard(container, faction) {
    const showBtn = document.getElementById('gear-focus-show');
    if (focusCollapsed) {
        container.innerHTML = '';
        container.classList.remove('mb-4');
        if (showBtn) showBtn.innerHTML = `<button onclick="toggleGearFocus()"
            class="text-xs font-bold text-game-warning border border-game-warning/50 rounded px-2 py-1 bg-game-warning/20 hover:bg-game-warning/50 hover:text-white whitespace-nowrap">🎯 Show focus tip</button>`;
        return;
    }
    if (showBtn) showBtn.innerHTML = '';

    const focus = getSuggestedFocus(faction);
    if (!focus) {
        container.classList.add('mb-4');
        container.innerHTML = `
            <div class="relative border-2 border-green-700 rounded-lg p-4 bg-green-900/10 text-sm">
                ${focusCloseBtn}
                ✅ <b>Field kit complete!</b> Every tech gear piece is yours — Tier-3 raids and Artifacts and are the next frontier.
            </div>`;
        return;
    }

    const { item, status } = focus;
    const inputRows = status.recipe.inputs.map(input => {
        const have = gameState.character.inventory[input.id] || 0;
        const ok = have >= input.qty;
        const inputItem = findItem('id', input.id);
        let sourceLine = '';
        if (!ok) {
            const desc = describeMaterialSource({ id: input.id, short: input.qty - have });
            if (desc) sourceLine = `<span class="text-[11.5px] text-blue-300/80">→ ${desc}</span>`;
        }
        return `
            <div class="flex gap-2 items-baseline text-[13px] py-0.5">
                <span class="${ok ? 'text-game-success' : 'text-game-danger'} font-bold text-nowrap">${ok ? '✔' : '✘'} ${have}/${input.qty}</span>
                <span class="text-nowrap">${inputItem?.icon || ''} ${inputItem?.name || input.id}</span>
                ${sourceLine}
            </div>`;
    }).join('');

    container.classList.add('mb-4');
    container.innerHTML = `
        <div class="relative border-2 border-game-warning rounded-lg p-4" style="background:rgba(245,158,11,0.1);">
            ${focusCloseBtn}
            <h3 class="m-0 mb-1 pr-7 text-game-warning text-[15px] font-bold">🎯 Suggested focus: ${item.icon || ''} ${item.name}</h3>
            <div class="text-xs text-gray-400 mb-2">Closest tech gear to completion — ${status.recipe.inputs.length - status.missing.length} of ${status.recipe.inputs.length} materials ready</div>
            ${inputRows}
        </div>`;
}

// Inventory card cell (createInventoryCard verbatim, expanded or collapsed).
// Two onclick retargets: post-craft refresh goes to this page instead of the
// crafting modal, and the card's collapse toggle goes through toggleGearExpand
// so the state survives this page's re-renders.
function cardCell(item, status, { isFocus = false, collapsed = false, isArtifact = false, stackable = false } = {}) {
    renderedExpanded.set(item.id, !collapsed);

    const recipeMeta = {
        ...status.recipe,
        itemType: isArtifact ? 'artifact' : item.type,
        itemRarity: item.rarity,
        techniqueId: item.techniqueId
    };
    const materials = findItemsById(status.recipe.inputs.map(i => i.id));
    // Stackables (consumables) never "complete": keep showing recipe progress
    const options = { collapsed, progress: (!stackable && status.owned) ? 1 : null, isFocus };
    let cardHtml = createInventoryCard(item, 'crafting', recipeMeta, materials, options);
    cardHtml = cardHtml
        .replaceAll('showCraftingModal();', 'renderGearTab();')
        .replaceAll('toggleInventoryCardCollapse(this);', `toggleGearExpand('${item.id}');`);

    // The card's own action button now covers the missing-materials case
    // (active CRAFT COMPONENTS inside the card), so no extra button here
    return `
        <div class="relative">
            ${cardHtml}
        </div>`;
}

/** The selected skill's row of the gear matrix, or null if the faction has none. */
function activeColumn(groupId, faction) {
    const skillId = activeSkillTab(groupId, faction);
    return getGearMatrix(faction).find(column => column.skillId === skillId) || null;
}

function renderMatrix(container, faction) {
    const focus = getSuggestedFocus(faction);
    const column = activeColumn(KIT_TABS, faction);

    const cells = (column?.techniques || []).map(({ item }) => {
        const status = getGearStatus(item.id);
        const isFocus = focus && focus.item.id === item.id;
        // Craftable and focus items open by default; owned and distant
        // items start collapsed. A player toggle overrides either way.
        const defaultExpanded = !status.owned && (status.craftable || isFocus);
        const expanded = gearCardOverrides.has(item.id)
            ? gearCardOverrides.get(item.id)
            : defaultExpanded;
        return cardCell(item, status, { isFocus, collapsed: !expanded });
    }).join('');

    container.innerHTML = `
        ${skillTabStrip(KIT_TABS, faction)}
        <div class="${THREE_UP}" style="${THREE_UP_TRACKS}">
            ${cells}
        </div>`;
}

function renderArtifacts(container) {
    const statuses = getArtifactStatuses();
    if (!statuses.length) {
        container.innerHTML = '<div class="text-gray-400 text-sm">No artifact recipes for this faction yet.</div>';
        return;
    }

    const cards = statuses.map(({ artifact, status, hasBlueprint, copiesOwned }) => {
        // Artifacts without a recipe (not craftable yet) get a minimal tile
        // instead of an orphaned footer
        if (!status.recipe) {
            return `
                <div class="rounded-lg border border-gray-700 bg-white/5 p-3 text-[12px] text-gray-400 flex flex-col gap-1">
                    <span class="font-bold text-gray-300">${artifact.icon || ''} ${artifact.name}</span>
                    <span>No recipe — drop only</span>
                    ${copiesOwned > 0 ? `<span class="text-game-gold">owned ×${copiesOwned}</span>` : ''}
                </div>`;
        }

        // UNLOCK is the real gate now: only unlocked designs are expandable
        // (their craft controls are actionable) and open by default. Locked
        // designs stay collapsed - the card component forces that and surfaces
        // the unlock control in the collapsed view.
        const unlocked = isArtifactUnlocked(artifact.id);
        const defaultExpanded = unlocked;
        const expanded = gearCardOverrides.has(artifact.id)
            ? gearCardOverrides.get(artifact.id)
            : defaultExpanded;
        renderedExpanded.set(artifact.id, expanded);

        const recipeMeta = { ...status.recipe, itemType: 'artifact', itemRarity: artifact.rarity };
        const materials = findItemsById(status.recipe.inputs.map(i => i.id));
        // A blueprint identifies the design (shows its name + the unlock button);
        // fully-undiscovered designs (no blueprint, not unlocked) stay <unknown>.
        const cardOptions = unlocked
            ? { collapsed: !expanded }
            : hasBlueprint
                ? { collapsed: true }
                : { collapsed: true, displayName: '&lt;unknown&gt; — No Blueprint', hideToggle: true };
        const cardHtml = createInventoryCard(artifact, 'crafting', recipeMeta, materials, cardOptions)
            .replaceAll('showCraftingModal();', 'renderGearTab();')
            .replaceAll('toggleInventoryCardCollapse(this);', `toggleGearExpand('${artifact.id}');`);

        return `
            <div class="relative">
                ${cardHtml}
            </div>`;
    }).join('');

    container.innerHTML = `
        <div class="grid gap-3 justify-between" style="grid-template-columns: repeat(auto-fit, 13rem);">
            ${cards}
        </div>`;
}

// HP field supplies (purchase-only credit sinks; energy is bought one-step from
// the header's ⚡ popup). Their own tab beside the Field Kit: they are bought,
// not crafted, and share nothing with the skill-tabbed panes.
const FIELD_SUPPLY_IDS = ['field-health-stim', 'recovery-injector-mk2', 'advanced-recovery-serum'];

function renderSupplies(container) {
    const inventory = gameState.character?.inventory || {};

    const supplyCards = FIELD_SUPPLY_IDS.map(id => {
        const item = findItem('id', id);
        if (!item) return '';
        const qty = inventory[id] || 0;
        // Re-render this page after buying so owned counts stay fresh
        const cardHtml = createInventoryCard({ ...item, quantity: qty}, 'purchase', null, null, {collapsed: true})
            .replaceAll(`purchaseConsumable('${id}')`, `purchaseConsumable('${id}'); renderGearTab();`);
        return `<div class="relative">${cardHtml}</div>`;
    }).join('');

    container.innerHTML = `
        <div class="${THREE_UP}" style="${THREE_UP_TRACKS}">
            ${supplyCards}
        </div>`;
}

// Technique consumables: one per technique, on the same skill tabs as the Field
// Kit. Crafted from materials; auto-deploy on technique success in raids.
function renderConsumables(container, faction) {
    const inventory = gameState.character?.inventory || {};
    const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1);
    const consumables = config[`itemsEpicConsumable${capitalize(faction)}`] || [];
    const column = activeColumn(CONSUMABLE_TABS, faction);

    const cells = (column?.techniques || []).map(({ technique }) => {
        const item = consumables.find(c => c.techniqueId === technique.id);
        if (!item) return '';
        const status = getGearStatus(item.id);
        if (!status.recipe) return '';
        const qty = inventory[item.id] || 0;
        // Ready-to-craft items open by default; the rest stay collapsed
        const defaultExpanded = status.missing.length === 0 && qty === 0;
        const expanded = gearCardOverrides.has(item.id)
            ? gearCardOverrides.get(item.id)
            : defaultExpanded;
        return cardCell(item, status, { collapsed: !expanded, stackable: true });
    }).join('');

    container.innerHTML = `
        ${skillTabStrip(CONSUMABLE_TABS, faction)}
        <div class="${THREE_UP}" style="${THREE_UP_TRACKS}">
            ${cells}
        </div>`;
}

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

// Gear sub-tab state (Field Kit / Field Supplies / Artifacts / Consumables) -
// survives re-renders; buttons and panes are static markup in index.html
let activeGearSubTab = 'field-kit';

const GEAR_SUBTAB_PANES = {
    'field-kit': 'gear-pane-field-kit',
    'supplies': 'gear-pane-supplies',
    'artifacts': 'gear-pane-artifacts',
    'consumables': 'gear-pane-consumables'
};

function applyGearSubTab() {
    document.querySelectorAll('[data-gear-tab]').forEach(btn =>
        btn.classList.toggle('active', btn.dataset.gearTab === activeGearSubTab));
    Object.entries(GEAR_SUBTAB_PANES).forEach(([name, paneId]) =>
        document.getElementById(paneId)?.classList.toggle('hidden', name !== activeGearSubTab));
}

export function setGearSubTab(name) {
    if (!GEAR_SUBTAB_PANES[name]) return;
    activeGearSubTab = name;
    applyGearSubTab();
}

export function renderGearTab() {
    const faction = gameState.character?.faction;
    const strip = document.getElementById('gear-goal-strip');
    const focusEl = document.getElementById('gear-focus');
    const matrixEl = document.getElementById('gear-matrix');
    const artifactsEl = document.getElementById('gear-artifacts');
    const suppliesEl = document.getElementById('gear-supplies');
    const consumablesEl = document.getElementById('gear-consumables');
    if (!strip || !faction) return;

    renderGoalStrip(strip, faction);
    renderFocusCard(focusEl, faction);
    renderMatrix(matrixEl, faction);
    renderArtifacts(artifactsEl);
    if (suppliesEl) renderSupplies(suppliesEl);
    if (consumablesEl) renderConsumables(consumablesEl, faction);
    applyGearSubTab();

    // Attach Tippy tooltips to the freshly-rendered card artwork
    window.reinitializeInventoryTooltips?.();
}

export function toggleGearExpand(itemId) {
    const current = renderedExpanded.get(itemId) ?? false;
    gearCardOverrides.set(itemId, !current);
    renderGearTab();
}

export function toggleGearFocus() {
    focusCollapsed = !focusCollapsed;
    renderGearTab();
}

if (typeof window !== 'undefined') {
    window.renderGearTab = renderGearTab;
    window.toggleGearExpand = toggleGearExpand;
    window.toggleGearFocus = toggleGearFocus;
    window.setGearSubTab = setGearSubTab;
}
